<template>
  <div class="media-container" data-cy="media-card">
    <div
      class="media-link"
      :class="{ selected: selectable && selected }"
      :title="name"
      @click="emit('click')"
    >
      <div class="media-thumbnail">
        <img
          v-if="thumbnailUrl"
          :src="thumbnailUrl"
          :alt="name"
          class="thumbnail-image"
        />
        <div v-else class="thumbnail-placeholder">
          <Spinner
            v-if="!thumbnailProcessed"
            message=""
            class="thumbnail-spinner"
          />
          <i v-else :class="placeholderIcon" class="thumbnail-icon"></i>
        </div>
        <i v-if="showPlayIcon" class="fa-solid fa-play play-icon"></i>
        <div
          v-if="selectable"
          class="select-overlay"
          data-cy="media-card-select"
          @click.stop
        >
          <Checkbox
            :model-value="selected"
            @update:model-value="emit('toggle-select')"
          />
        </div>
        <div class="buttons-container">
          <Tooltip placement="top" :disabled="breakpoints().sm">
            <template #trigger>
              <SeraButton
                icon="fa-regular fa-trash-alt"
                @click="handleDelete"
              />
            </template>
            <template #tooltip> Delete {{ mediaTypeLabel }} </template>
          </Tooltip>
          <span class="bottom-separator"> | </span>
          <Tooltip placement="top">
            <template #trigger>
              <SeraButton
                :icon="visibilityIcon"
                data-cy="media-card-visibility-toggle"
                @click="handleVisibilityToggle"
              />
            </template>
            <template #tooltip>
              {{ visibilityTooltip }}
            </template>
          </Tooltip>
        </div>
      </div>
    </div>
    <div v-if="name" class="media-name" :title="name">{{ name }}</div>
    <div class="date-created">{{ formattedDate }}</div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import SeraButton from "@/components/Button.vue";
import Checkbox from "@/components/Checkbox.vue";
import Spinner from "@/components/Spinner.vue";
import Tooltip from "@/components/Tooltip.vue";
import { breakpoints } from "@/mixins";

export type MediaType = "photo" | "video" | "document";

const props = withDefaults(
  defineProps<{
    thumbnailUrl?: string;
    thumbnailProcessed?: boolean;
    name?: string;
    createdAt?: string;
    customerVisible?: boolean;
    mediaType?: MediaType;
    selectable?: boolean;
    selected?: boolean;
  }>(),
  {
    thumbnailUrl: "",
    thumbnailProcessed: true,
    name: "",
    createdAt: "",
    customerVisible: false,
    mediaType: "document",
    selectable: false,
    selected: false
  }
);

const emit = defineEmits<{
  click: [];
  delete: [];
  "toggle-visibility": [];
  "toggle-select": [];
}>();

const mediaTypeLabel = computed(() => {
  const labels: Record<MediaType, string> = {
    photo: "Photo",
    video: "Video",
    document: "Document"
  };
  return labels[props.mediaType];
});

const placeholderIcon = computed(() => {
  const icons: Record<MediaType, string> = {
    photo: "fa-regular fa-image",
    video: "fa-solid fa-video",
    document: "fa-regular fa-file"
  };
  return icons[props.mediaType];
});

const showPlayIcon = computed(() => props.mediaType === "video");

const visibilityIcon = computed(() => {
  return props.customerVisible
    ? "fa-regular fa-eye"
    : "fa-regular fa-eye-slash";
});

const visibilityTooltip = computed(() => {
  return props.customerVisible ? "Visible to customer" : "Hidden from customer";
});

const formattedDate = computed(() => {
  if (!props.createdAt) return "";
  const datetime = window.vue_app?.config?.globalProperties?.datetime;
  if (datetime) {
    return datetime(props.createdAt);
  }
  return new Date(props.createdAt).toLocaleString();
});

function handleDelete(e: Event) {
  e.preventDefault();
  e.stopPropagation();
  emit("delete");
}

// Stop the click from bubbling to the card wrapper, which would also open the
// viewer. The eye icon is a single-purpose control: toggle visibility only.
function handleVisibilityToggle(e: Event) {
  e.preventDefault();
  e.stopPropagation();
  emit("toggle-visibility");
}
</script>

<style scoped>
.media-container {
  @apply text-center flex flex-col;
}

.media-link {
  @apply flex flex-col rounded no-underline cursor-pointer transition-shadow duration-150;
  color: var(--sera-color-font-body);
  border: 1px solid var(--sera-color-fields-border);
  background: var(--sera-color-neutral-gray-light-gray-100);
}

.media-link.selected {
  border-color: #2563eb;
}

/* Blue wash + ring drawn over the thumbnail so a selected card reads at a
   glance, even on busy photos. The ring is an inset shadow (kept inside the
   card) rather than an outer box-shadow, so it's never clipped by the grid
   container at the edge columns. Non-interactive so the thumbnail still opens
   the viewer. */
.media-link.selected .media-thumbnail::after {
  content: "";
  @apply absolute inset-0 pointer-events-none;
  background: rgba(37, 99, 235, 0.12);
  box-shadow: inset 0 0 0 2px #2563eb;
}

.media-thumbnail {
  @apply w-full aspect-square flex items-center justify-center rounded relative overflow-hidden;
  background: var(--sera-color-neutral-gray-light-gray-200);
}

.thumbnail-image {
  @apply w-full h-full object-contain;
  background-color: var(--sera-color-neutral-gray-light-gray-200);
}

.thumbnail-placeholder {
  @apply flex items-center justify-center w-full h-full;
}

.thumbnail-icon {
  @apply text-3xl;
  color: var(--sera-color-neutral-gray-light-gray-500);
}

.thumbnail-spinner {
  @apply scale-75;
}

.play-icon {
  @apply absolute text-3xl opacity-0 transition-opacity duration-200;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.media-link:hover .play-icon {
  @apply opacity-100;
}

.media-thumbnail:not(:has(.thumbnail-image)) .play-icon {
  @apply opacity-100;
  color: var(--sera-color-neutral-gray-light-gray-600);
  text-shadow: none;
}

.media-link:hover .media-thumbnail:not(:has(.thumbnail-image)) .play-icon {
  color: var(--sera-color-text-links);
}

/* Per-card delete / visibility controls stay out of the way until the card is
   hovered, focused, or selected — keeps the grid clean rather than stamping a
   permanent toolbar over every thumbnail. */
.buttons-container {
  @apply flex flex-row items-center h-[30px] justify-evenly;
  @apply absolute bottom-0 left-0 right-0;
  @apply opacity-0 transition-opacity duration-150;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(3px);
}

.media-link:hover .buttons-container,
.media-thumbnail:focus-within .buttons-container,
.media-link.selected .buttons-container {
  @apply opacity-100;
}

.select-overlay {
  @apply absolute top-2 left-2 z-10;
}

/* Size up the standard Checkbox's styled box (same approach the datatable
   selection checkbox uses) and add a drop shadow so it stays legible on any
   photo. The box itself is the control — no surrounding chip. */
.select-overlay :deep(.checkbox-container) {
  /* The base component's w-4 h-4 are !important (Tailwind important mode), so
     the size override has to be !important too — same as the datatable
     selection checkbox. */
  width: 22px !important;
  height: 22px !important;
  margin-right: 0;
  font-size: 15px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
}

.media-name {
  @apply text-xs font-medium text-gray-800 overflow-hidden text-ellipsis px-1 mt-2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.35;
  /* Reserve two lines so the date below lines up across every card in a row,
     regardless of whether a filename wraps to one line or two. */
  min-height: 2.7em;
}

.date-created {
  @apply text-[11px] text-gray-500 leading-tight mt-1;
}
</style>
